import { useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";
import type { PitchCategory } from "@/types/camping";

interface BulkCreateForm {
  code_prefix: string;
  number_from: string;
  number_to: string;
  number_width: string;
  label_template: string;
  category_id: string;
  size_sqm: string;
  has_electricity: boolean;
  has_water: boolean;
  has_sewage: boolean;
}

const EMPTY_FORM: BulkCreateForm = {
  code_prefix: "",
  number_from: "1",
  number_to: "",
  number_width: "2",
  label_template: "Platz {code}",
  category_id: "",
  size_sqm: "",
  has_electricity: false,
  has_water: false,
  has_sewage: false,
};

interface BulkCreateResult {
  created: number;
  skipped: number;
  message: string;
  errors: { code: string | null; message: string }[];
}

export interface BulkCreatePitchModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  categories: PitchCategory[];
}

export function BulkCreatePitchModal({
  open,
  onClose,
  onCreated,
  categories,
}: BulkCreatePitchModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkCreateResult | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError(null);
    setResult(null);
    setSubmitting(false);
  }, [open]);

  const previewCodes = useMemo(() => {
    const from = Number(form.number_from);
    const to = Number(form.number_to);
    const width = Number(form.number_width) || 2;
    const prefix = form.code_prefix.trim().toUpperCase();
    if (!prefix || !Number.isFinite(from) || !Number.isFinite(to) || to < from) return [];
    const codes: string[] = [];
    for (let n = from; n <= to && codes.length < 5; n += 1) {
      codes.push(`${prefix}${String(n).padStart(width, "0")}`);
    }
    const total = to - from + 1;
    if (total > 5) codes.push("…");
    return codes;
  }, [form.code_prefix, form.number_from, form.number_to, form.number_width]);

  const count = useMemo(() => {
    const from = Number(form.number_from);
    const to = Number(form.number_to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return 0;
    return to - from + 1;
  }, [form.number_from, form.number_to]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    const from = Number(form.number_from);
    const to = Number(form.number_to);
    if (!form.code_prefix.trim()) {
      setError("Bitte ein Code-Präfix angeben.");
      return;
    }
    if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) {
      setError("Ungültiger Nummernbereich.");
      return;
    }
    if (count > 200) {
      setError("Maximal 200 Plätze pro Vorgang.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<BulkCreateResult>("/api/v1/pitches/bulk", {
        code_prefix: form.code_prefix.trim(),
        number_from: from,
        number_to: to,
        number_width: Number(form.number_width) || 2,
        label_template: form.label_template.trim() || null,
        category_id: form.category_id || null,
        size_sqm: form.size_sqm ? Number(form.size_sqm) : null,
        has_electricity: form.has_electricity,
        has_water: form.has_water,
        has_sewage: form.has_sewage,
      });
      setResult(res);
      if (res.created > 0) onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Mehrere Stellplätze anlegen"
      description="Plätze mit fortlaufenden Codes in einem Schritt erstellen"
      size="lg"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Code-Präfix *</span>
            <input
              className="eh-input"
              placeholder="z. B. A"
              value={form.code_prefix}
              onChange={(e) => setForm((f) => ({ ...f, code_prefix: e.target.value }))}
              required
              autoFocus
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Stellen (Auffüllung)</span>
            <input
              className="eh-input"
              type="number"
              min={1}
              max={4}
              value={form.number_width}
              onChange={(e) => setForm((f) => ({ ...f, number_width: e.target.value }))}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Nummer von *</span>
            <input
              className="eh-input"
              type="number"
              min={1}
              value={form.number_from}
              onChange={(e) => setForm((f) => ({ ...f, number_from: e.target.value }))}
              required
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Nummer bis *</span>
            <input
              className="eh-input"
              type="number"
              min={1}
              value={form.number_to}
              onChange={(e) => setForm((f) => ({ ...f, number_to: e.target.value }))}
              required
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block text-label-secondary">Bezeichnung (Vorlage)</span>
            <input
              className="eh-input"
              value={form.label_template}
              onChange={(e) => setForm((f) => ({ ...f, label_template: e.target.value }))}
              placeholder="Platz {code}"
            />
            <span className="mt-1 block text-xs text-label-tertiary">
              {"{code}"} wird durch den Platzcode ersetzt.
            </span>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Kategorie</span>
            <select
              className="eh-select-input"
              value={form.category_id}
              onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
            >
              <option value="">Platzkategorie wählen…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Größe m²</span>
            <input
              className="eh-input"
              value={form.size_sqm}
              onChange={(e) => setForm((f) => ({ ...f, size_sqm: e.target.value }))}
            />
          </label>
        </div>

        {previewCodes.length > 0 ? (
          <p className="text-sm text-label-secondary">
            {count > 0 ? (
              <>
                Es werden <strong className="text-label">{count}</strong> Plätze angelegt, z. B.{" "}
                {previewCodes.join(", ")}
              </>
            ) : null}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-4 text-sm text-label">
          {(
            [
              ["has_electricity", "Strom"],
              ["has_water", "Wasser"],
              ["has_sewage", "Abwasser"],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2">
              <input
                type="checkbox"
                className="rounded border-separator text-accent focus:ring-accent"
                checked={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
              />
              {label}
            </label>
          ))}
        </div>

        {result ? (
          <div className="rounded-lg border border-separator bg-fill-tertiary/40 p-3 text-sm">
            <p className="font-medium text-label">{result.message}</p>
            {result.errors.length > 0 ? (
              <ul className="mt-2 max-h-32 overflow-y-auto text-status-red">
                {result.errors.map((err, i) => (
                  <li key={`${err.code ?? i}-${err.message}`}>
                    {err.code ? `${err.code}: ` : ""}
                    {err.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {result?.created ? "Schließen" : "Abbrechen"}
          </Button>
          {!result?.created ? (
            <Button type="submit" loading={submitting}>
              {count > 0 ? `${count} Plätze anlegen` : "Plätze anlegen"}
            </Button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
